import type { CSSProperties } from 'react';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useGraphEntities } from '../contexts/GraphEntitiesContext';
import { getNodeColors } from '@cade/graph-logic';

/**
 * Returns a translucent version of a category color. Accepts the `#rrggbb`
 * hex values produced by getNodeColors and falls back to the original string
 * (already-translucent or unexpected formats are returned unchanged).
 */
function withAlpha(color: string, alpha: number): string {
  const match = /^#([0-9a-fA-F]{6})$/.exec(color.trim());
  if (!match || !match[1]) return color;
  const int = parseInt(match[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface Props {
  label: string;
  nodeId: string;
  aliases?: string[];
  entityType?: string;
}

export function EntityChip({ label, nodeId, aliases, entityType }: Props) {
  const { highlightNode, centerOnNode, highlightedEntityLabel } = useGraphEntities();

  const [hovered, setHovered] = useState(false);

  // Chip is "active" when directly hovered OR when graph node hover matches this label
  const graphHighlighted = highlightedEntityLabel != null &&
    label.toLowerCase() === highlightedEntityLabel.toLowerCase();
  const isActive = hovered || graphHighlighted;

  const handleMouseEnter = useCallback(() => { highlightNode(nodeId); setHovered(true); }, [highlightNode, nodeId]);
  const handleMouseLeave = useCallback(() => { highlightNode(null); setHovered(false); }, [highlightNode]);
  const handleClick = useCallback(() => centerOnNode(nodeId), [centerOnNode, nodeId]);

  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  );
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.getAttribute('data-theme') === 'dark');
    const observer = new MutationObserver(update);
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const chipStyle = useMemo(() => {
    if (!entityType) return undefined;
    // Node fill (category color) drives BOTH the underline and the soft hover/active
    // tint, so a chip reads in the same color family as its node in the graph.
    // (The graph edge/stroke color is deliberately not used here.)
    const { fill } = getNodeColors(entityType, isDark);
    const style: CSSProperties = {
      // Underline matches the entity's graph-node fill color.
      textDecorationLine: 'underline',
      textDecorationColor: fill,
      textDecorationThickness: '2px',
      textUnderlineOffset: '2px',
    };
    if (isActive) {
      // Soft/translucent version of the same category fill color for hover/active.
      style.background = withAlpha(fill, isDark ? 0.28 : 0.4);
    }
    return style;
  }, [entityType, isDark, isActive]);

  return (
    <span
      className="entity-chip"
      data-entity-id={nodeId}
      data-entity-label={label}
      data-entity-aliases={aliases ? JSON.stringify(aliases) : undefined}
      style={chipStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {label}
    </span>
  );
}
